/**
 * A canvas note — `note.text` or `note.header`.
 *
 * An annotation, not a step: no handles, never executed, never validated. It
 * is saved in the graph like any node so the explanation travels with the
 * pipeline version it explains. Double-click to edit in place; Escape or a
 * click outside finishes. A locked pipeline renders it read-only.
 */

import { useEffect, useRef, useState, type FocusEvent, type KeyboardEvent } from "react";
import { NodeResizer, type NodeProps } from "@xyflow/react";
import { Button, Group, SegmentedControl, Stack, Text, TextInput, Textarea } from "@mantine/core";
import { NOTE_COLORS, noteBackground } from "./nodeRegistry";

export type NotePatch = { title?: string; text?: string; color?: string };

const FIELD_STYLE = {
  width: "100%",
  border: "none",
  outline: "none",
  background: "transparent",
  font: "inherit",
  color: "inherit",
  resize: "none" as const,
  padding: 0,
};

export function NoteNode({ id, data, selected }: NodeProps) {
  const d = data as Record<string, unknown>;
  const kind = String(d.kind ?? "");
  const config = (d.config ?? {}) as Record<string, unknown>;
  const editable = Boolean(d.editable);
  const onNoteChange = d.onNoteChange as ((id: string, patch: NotePatch) => void) | undefined;
  const withHeader = kind === "note.header";
  const title = String(config.title ?? "");
  const text = String(config.text ?? "");

  const [editing, setEditing] = useState(false);
  const [draftTitle, setDraftTitle] = useState(title);
  const [draftText, setDraftText] = useState(text);
  const boxRef = useRef<HTMLDivElement>(null);
  const firstRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (!editing) {
      setDraftTitle(title);
      setDraftText(text);
    }
  }, [title, text, editing]);
  useEffect(() => {
    if (editing) firstRef.current?.focus();
  }, [editing]);
  // A locked pipeline cannot be in the middle of an edit.
  useEffect(() => {
    if (!editable) setEditing(false);
  }, [editable]);

  const finish = () => {
    setEditing(false);
    if (draftTitle !== title || draftText !== text) {
      onNoteChange?.(id, withHeader ? { title: draftTitle, text: draftText } : { text: draftText });
    }
  };
  const onBlur = (e: FocusEvent) => {
    if (boxRef.current?.contains(e.relatedTarget as globalThis.Node | null)) return;
    finish();
  };
  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      finish();
    }
  };
  const startEditing = () => {
    if (editable) setEditing(true);
  };

  const empty = !title && !text;
  return (
    <>
      <NodeResizer isVisible={selected && editable} minWidth={120} minHeight={60} />
      <div
        ref={boxRef}
        onDoubleClick={startEditing}
        onClick={() => {
          if (selected) startEditing();
        }}
        style={{
          width: "100%",
          height: "100%",
          boxSizing: "border-box",
          padding: "10px 12px",
          background: noteBackground(config.color),
          color: "#1f2933",
          borderRadius: 6,
          border: selected
            ? "1px solid var(--mantine-primary-color-filled)"
            : "1px solid rgba(0,0,0,0.08)",
          boxShadow: selected ? "0 2px 8px rgba(0,0,0,0.15)" : "0 1px 3px rgba(0,0,0,0.08)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          gap: 4,
          fontSize: 13,
          lineHeight: 1.4,
        }}
      >
        {editing ? (
          <>
            {withHeader && (
              <input
                ref={(el) => {
                  firstRef.current = el;
                }}
                className="nodrag"
                value={draftTitle}
                placeholder="Header"
                onChange={(e) => setDraftTitle(e.currentTarget.value)}
                onBlur={onBlur}
                onKeyDown={onKeyDown}
                style={{ ...FIELD_STYLE, fontWeight: 700, fontSize: 14 }}
              />
            )}
            <textarea
              ref={(el) => {
                if (!withHeader) firstRef.current = el;
              }}
              className="nodrag nowheel"
              value={draftText}
              placeholder="Write a note…"
              onChange={(e) => setDraftText(e.currentTarget.value)}
              onBlur={onBlur}
              onKeyDown={onKeyDown}
              style={{ ...FIELD_STYLE, flex: 1 }}
            />
          </>
        ) : (
          <>
            {withHeader && (title || empty) && (
              <div style={{ fontWeight: 700, fontSize: 14, whiteSpace: "pre-wrap" }}>
                {title || <span style={{ opacity: 0.45 }}>Header</span>}
              </div>
            )}
            <div style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere", flex: 1 }}>
              {text ||
                (empty && (
                  <span style={{ opacity: 0.45 }}>
                    {editable ? "Double-click to write a note" : "Empty note"}
                  </span>
                ))}
            </div>
          </>
        )}
      </div>
    </>
  );
}

/** The inspector for a selected note: header, body, colour. */
export function NoteInspector({
  kind,
  config,
  editable,
  onChange,
  onDelete,
}: {
  kind: string;
  config: Record<string, unknown>;
  editable: boolean;
  onChange: (key: string, value: unknown) => void;
  onDelete: () => void;
}) {
  return (
    <Stack gap="xs">
      <Text fw={600}>{kind === "note.header" ? "Text with header" : "Text"}</Text>
      <Text size="xs" c="dimmed">
        A note on the canvas. It has no inputs or outputs and never runs.
      </Text>
      {kind === "note.header" && (
        <TextInput
          label="Header"
          value={String(config.title ?? "")}
          disabled={!editable}
          onChange={(e) => onChange("title", e.currentTarget.value)}
        />
      )}
      <Textarea
        label="Text"
        autosize
        minRows={4}
        value={String(config.text ?? "")}
        disabled={!editable}
        onChange={(e) => onChange("text", e.currentTarget.value)}
      />
      <div>
        <Text size="sm" fw={500} mb={4}>
          Colour
        </Text>
        <SegmentedControl
          size="xs"
          fullWidth
          disabled={!editable}
          value={String(config.color ?? "yellow")}
          onChange={(v) => onChange("color", v)}
          data={NOTE_COLORS.map((c) => ({ value: c.value, label: c.label }))}
        />
      </div>
      <Group justify="flex-end">
        <Button size="xs" color="red" variant="light" disabled={!editable} onClick={onDelete}>
          Delete note
        </Button>
      </Group>
    </Stack>
  );
}
